/* eslint-disable no-unused-vars */
import React from "react";
import moment from "moment";
import { WEEKDAYS, isSelectableDay, isWeekdayFullySelected, monthGrid } from "../../DeclarationCourse/sessionCalendarHelpers";
import { DV2 } from "../theme";
import { capitalizeFirstLetterSafe, monthLabel } from "../format";
import { useDv2Theme } from "../layout/Ui";

interface Props {
    month: moment.Moment;
    onMonthChange: (month: moment.Moment) => void;
    /** Premier mois navigable (YYYY-MM). */
    firstMonth: string;
    /** Dernier mois navigable (YYYY-MM). */
    lastMonth: string;
    /** Dates cochées (YYYY-MM-DD). */
    selected: string[];
    /** Premier jour sélectionnable. */
    minDate: string;
    onToggleDate: (date: string) => void;
    /** Raccourci par jour de semaine (plusieurs cours uniquement). */
    onToggleWeekday?: (isoWeekday: number) => void;
    /** Dates cochées en conflit avec un cours existant. */
    conflicts?: Set<string>;
    ariaLabel?: string;
}

/**
 * Grille du mois (maquettes 1b, 1i, 2b, 2i) : sept colonnes du lundi au
 * dimanche, rangée de raccourcis par jour de semaine, aujourd'hui entouré,
 * jours hors période grisés, dates en conflit en orange.
 */
const MonthCalendar = ({ month, onMonthChange, firstMonth, lastMonth, selected, minDate, onToggleDate, onToggleWeekday, conflicts, ariaLabel }: Props) => {
    const { primary, fontFamily } = useDv2Theme();
    const key = month.format("YYYY-MM");
    const canPrevious = key > firstMonth;
    const canNext = key < lastMonth;
    const today = moment().format("YYYY-MM-DD");

    const navButton = (label: string, enabled: boolean, onClick: () => void, glyph: string) => (
        <button
            type="button"
            aria-label={label}
            disabled={!enabled}
            onClick={onClick}
            style={{ background: "none", border: "none", color: enabled ? primary : DV2.border, fontSize: 22, cursor: enabled ? "pointer" : "default", padding: "0 6px", fontFamily }}
        >
            {glyph}
        </button>
    );

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, fontFamily }} role="group" aria-label={ariaLabel ?? `Calendrier, ${monthLabel(month)}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {navButton("Mois précédent", canPrevious, () => onMonthChange(month.clone().subtract(1, "month")), "‹")}
                <span style={{ fontWeight: 800, fontSize: 16 }} aria-live="polite">{monthLabel(month)}</span>
                {navButton("Mois suivant", canNext, () => onMonthChange(month.clone().add(1, "month")), "›")}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
                {WEEKDAYS.map((weekday) => {
                    const active = onToggleWeekday ? isWeekdayFullySelected(selected, month, weekday.iso, minDate) : false;
                    const common: React.CSSProperties = { padding: "8px 0", textAlign: "center", borderRadius: 10, fontSize: 13, fontWeight: active ? 800 : 700, fontFamily };
                    if (!onToggleWeekday) {
                        return <div key={weekday.iso} aria-hidden style={{ ...common, color: DV2.muted }}>{weekday.short}</div>;
                    }
                    return (
                        <button
                            key={weekday.iso}
                            type="button"
                            aria-pressed={active}
                            aria-label={`Tous les ${weekday.long}s du mois`}
                            title={`Cocher tous les ${weekday.long}s du mois`}
                            onClick={() => onToggleWeekday(weekday.iso)}
                            style={{ ...common, cursor: "pointer", border: active ? "1px solid transparent" : "1px solid #C9D3E0", background: active ? primary : "#fff", color: active ? "#fff" : primary }}
                        >
                            {weekday.short}
                        </button>
                    );
                })}

                {monthGrid(month).flat().map((date, index) => {
                    if (!date) return <div key={`empty-${index}`} aria-hidden />;
                    const selectable = isSelectableDay(date, minDate);
                    const isSelected = selected.includes(date);
                    const isToday = date === today;
                    const inConflict = isSelected && conflicts?.has(date);
                    const style: React.CSSProperties = {
                        height: 44,
                        display: "grid",
                        placeItems: "center",
                        fontSize: 14,
                        fontWeight: isSelected ? 800 : 500,
                        borderRadius: 10,
                        fontFamily,
                        cursor: selectable ? "pointer" : "not-allowed",
                        border: inConflict ? "2px solid #E8973A" : isToday && !isSelected ? `2px solid ${primary}` : "1px solid transparent",
                        background: inConflict ? "#FBE9D9" : isSelected ? primary : selectable ? DV2.pageBg : "transparent",
                        color: inConflict ? DV2.warning : isSelected ? "#fff" : selectable ? DV2.text : "#C2CBD7",
                    };
                    return (
                        <button
                            key={date}
                            type="button"
                            role="checkbox"
                            aria-checked={isSelected}
                            aria-label={`${capitalizeFirstLetterSafe(moment(date).locale("fr").format("dddd D MMMM"))}${isToday ? ", aujourd'hui" : ""}${inConflict ? ", chevauchement" : ""}`}
                            disabled={!selectable}
                            onClick={() => onToggleDate(date)}
                            style={style}
                        >
                            {moment(date).date()}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default MonthCalendar;
